window.AdminBlogs = function AdminBlogs({ showNotification }) {
  const [blogs, setBlogs] = React.useState([]);
  const [mediaItems, setMediaItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('All');
  const [statusFilter, setStatusFilter] = React.useState('All');
  
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [editingBlog, setEditingBlog] = React.useState(null);
  const [imageTab, setImageTab] = React.useState('upload'); // 'upload', 'media', 'url'
  const [showMediaPicker, setShowMediaPicker] = React.useState(false);

  const initialFormState = {
    title: '',
    category: 'Railway Engineering',
    author: 'Dr. Aris Thorne, Lead R&D Engineer',
    readTime: '5 min read',
    date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    image: '',
    galleryImages: [],
    summary: '',
    content: '',
    badge: 'Article',
    status: 'published',
    keywords: ''
  };

  const [formData, setFormData] = React.useState(initialFormState);
  const [urlInput, setUrlInput] = React.useState('');

  const editorRef = React.useRef(null);
  const inlineImageInputRef = React.useRef(null);

  React.useEffect(() => {
    if (isModalOpen && editorRef.current) {
      editorRef.current.innerHTML = formData.content || '';
    }
  }, [isModalOpen]);

  const executeCommand = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: editorRef.current.innerHTML
      }));
    }
  };

  const [selectedEditorImg, setSelectedEditorImg] = React.useState(null);
  const [fontSizeInput, setFontSizeInput] = React.useState('');

  const cleanHtmlOutlines = (html) => {
    if (!html) return '';
    return html
      .replace(/outline:[^;"]+;?/g, '')
      .replace(/outline-offset:[^;"]+;?/g, '')
      .replace(/style=""/g, '');
  };

  const handleApplyFontSize = (val) => {
    if (!val) return;
    const size = parseInt(val);
    if (isNaN(size) || size <= 0) return;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && selection.toString()) {
      const htmlToInsert = `<span style="font-size: ${size}px;">${selection.toString()}</span>`;
      executeCommand('insertHTML', htmlToInsert);
    }
  };

  const handleEditorInput = (e) => {
    setFormData(prev => ({
      ...prev,
      content: cleanHtmlOutlines(e.target.innerHTML)
    }));
  };

  const handleEditorClick = (e) => {
    if (e.target.tagName === 'IMG') {
      if (selectedEditorImg && selectedEditorImg !== e.target) {
        selectedEditorImg.style.outline = '';
      }
      setSelectedEditorImg(e.target);
      e.target.style.outline = '2px dashed #ea580c';
      e.target.style.outlineOffset = '2px';
    } else {
      if (selectedEditorImg) {
        selectedEditorImg.style.outline = '';
        setSelectedEditorImg(null);
      }
    }
  };

  const resizeSelectedImage = (action) => {
    if (!selectedEditorImg) return;
    
    let currentWidth = selectedEditorImg.style.width || '100%';
    let widthVal = 100;
    let unit = '%';

    if (currentWidth.endsWith('%')) {
      widthVal = parseInt(currentWidth) || 100;
      unit = '%';
    } else if (currentWidth.endsWith('px')) {
      widthVal = parseInt(currentWidth) || 500;
      unit = 'px';
    }

    if (action === 'increase') {
      widthVal = Math.min(widthVal + 5, unit === '%' ? 100 : 1200);
    } else {
      widthVal = Math.max(widthVal - 5, unit === '%' ? 10 : 50);
    }

    selectedEditorImg.style.width = widthVal + unit;
    
    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: cleanHtmlOutlines(editorRef.current.innerHTML)
      }));
    }
  };

  const alignSelectedImage = (alignment) => {
    if (!selectedEditorImg) return;
    
    if (alignment === 'left') {
      selectedEditorImg.style.float = 'left';
      selectedEditorImg.style.display = 'inline-block';
      selectedEditorImg.style.margin = '10px 20px 10px 0';
    } else if (alignment === 'right') {
      selectedEditorImg.style.float = 'right';
      selectedEditorImg.style.display = 'inline-block';
      selectedEditorImg.style.margin = '10px 0 10px 20px';
    } else {
      selectedEditorImg.style.float = 'none';
      selectedEditorImg.style.display = 'block';
      selectedEditorImg.style.margin = '15px auto';
    }

    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: cleanHtmlOutlines(editorRef.current.innerHTML)
      }));
    }
  };

  const handleEditorDrop = (e) => {
    setTimeout(() => {
      if (editorRef.current) {
        setFormData(prev => ({
          ...prev,
          content: cleanHtmlOutlines(editorRef.current.innerHTML)
        }));
      }
    }, 100);
  };

  React.useEffect(() => {
    if (!isModalOpen && selectedEditorImg) {
      selectedEditorImg.style.outline = '';
      setSelectedEditorImg(null);
    }
  }, [isModalOpen]);

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await window.apiClient.uploadFile('media.php', formData);
      if (res.success && res.url) {
        executeCommand('insertHTML', `<img src="${res.url}" draggable="true" class="w-full max-w-xl mx-auto my-6 rounded-xl border border-slate-200 shadow-sm block" alt="Embedded Image" />`);
        showNotification('Image uploaded and inserted successfully.');
      } else {
        showNotification('Image upload failed.', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Image upload error.', 'error');
    } finally {
      if (inlineImageInputRef.current) inlineImageInputRef.current.value = '';
    }
  };

  const parseToDateInputVal = (dateStr) => {
    if (!dateStr) {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    const parsed = Date.parse(dateStr);
    if (!isNaN(parsed)) {
      const d = new Date(parsed);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatToDisplayDate = (dateVal) => {
    if (!dateVal) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const parts = dateVal.split('-');
    if (parts.length === 3) {
      const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    const date = new Date(dateVal);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  React.useEffect(() => {
    fetchBlogs();
    fetchMedia();
    const handleSync = () => {
      fetchBlogs();
      fetchMedia();
    };
    window.addEventListener('storage', handleSync);
    window.addEventListener('novatek_blogs_updated', handleSync);
    return () => {
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('novatek_blogs_updated', handleSync);
    };
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('blogs.php?admin=1');
      if (res && res.success) {
        setBlogs(res.data || []);
      }
    } catch (e) {
      console.warn('Blogs fetch warning:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const res = await window.apiClient.get('media.php');
      if (res && res.success) {
        setMediaItems(res.data || []);
      }
    } catch (e) {}
  };

  const handleOpenAddModal = () => {
    setEditingBlog(null);
    setFormData({
      ...initialFormState,
      date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      image: 'https://images.unsplash.com/photo-1515162305285-0293e4767cc2?auto=format&fit=crop&w=1200&q=80',
      galleryImages: ['https://images.unsplash.com/photo-1515162305285-0293e4767cc2?auto=format&fit=crop&w=1200&q=80'],
      keywords: ''
    });
    setUrlInput('');
    setImageTab('upload');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (blog) => {
    setEditingBlog(blog);
    const gallery = (blog.galleryImages && blog.galleryImages.length > 0) 
      ? blog.galleryImages 
      : (blog.image ? [blog.image] : []);
      
    setFormData({
      title: blog.title || '',
      category: blog.category || 'Railway Engineering',
      author: blog.author || '',
      readTime: blog.readTime || '5 min read',
      date: blog.date || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      image: blog.image || (gallery[0] || ''),
      galleryImages: gallery,
      summary: blog.summary || '',
      content: blog.content || '',
      badge: blog.badge || 'Article',
      status: blog.status || 'published',
      keywords: blog.keywords || ''
    });
    setUrlInput('');
    setImageTab('upload');
    setIsModalOpen(true);
  };

  const handleMultipleImageUploads = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    let count = 0;
    const newImages = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        newImages.push(evt.target.result);
        count++;
        if (count === files.length) {
          setFormData(prev => {
            const combined = [...(prev.galleryImages || []), ...newImages];
            return {
              ...prev,
              galleryImages: combined,
              image: prev.image || combined[0] || ''
            };
          });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleToggleMediaImage = (mediaUrl) => {
    setFormData(prev => {
      const current = prev.galleryImages || [];
      const exists = current.includes(mediaUrl);
      const updated = exists ? current.filter(img => img !== mediaUrl) : [...current, mediaUrl];
      return {
        ...prev,
        galleryImages: updated,
        image: updated[0] || ''
      };
    });
  };

  const handleAddUrlImage = () => {
    if (!urlInput || !urlInput.trim()) return;
    const trimmed = urlInput.trim();
    setFormData(prev => {
      const updated = [...(prev.galleryImages || []), trimmed];
      return {
        ...prev,
        galleryImages: updated,
        image: prev.image || updated[0] || ''
      };
    });
    setUrlInput('');
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData(prev => {
      const updated = (prev.galleryImages || []).filter((_, idx) => idx !== index);
      return {
        ...prev,
        galleryImages: updated,
        image: updated[0] || ''
      };
    });
  };

  const handleSetCoverImage = (imgUrl) => {
    setFormData(prev => {
      const current = prev.galleryImages || [];
      const filtered = current.filter(img => img !== imgUrl);
      const reordered = [imgUrl, ...filtered];
      return {
        ...prev,
        galleryImages: reordered,
        image: imgUrl
      };
    });
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      alert('Please fill in the Blog Title and Article Content.');
      return;
    }

    try {
      if (editingBlog) {
        // Edit Blog
        const res = await window.apiClient.put('blogs.php', {
          id: editingBlog.id,
          ...formData
        });
        if (res && res.success !== false) {
          if (showNotification) showNotification(`Blog '${formData.title}' updated successfully.`);
          setIsModalOpen(false);
          fetchBlogs();
          window.dispatchEvent(new CustomEvent('novatek_blogs_updated'));
        } else {
          alert(res.message || 'Update failed.');
        }
      } else {
        // Create Blog
        const res = await window.apiClient.post('blogs.php', formData);
        if (res && res.success !== false) {
          if (showNotification) showNotification(`New Blog '${formData.title}' published successfully.`);
          setIsModalOpen(false);
          fetchBlogs();
          window.dispatchEvent(new CustomEvent('novatek_blogs_updated'));
        } else {
          alert(res.message || 'Creation failed.');
        }
      }
    } catch (err) {
      console.warn('Blog save error:', err);
      alert(err.message || 'Operation failed.');
    }
  };

  const handleDeleteBlog = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete blog article:\n"${title}"?`)) return;
    try {
      const res = await window.apiClient.delete('blogs.php', { id });
      if (res && res.success !== false) {
        if (showNotification) showNotification(`Blog article deleted successfully.`);
        fetchBlogs();
        window.dispatchEvent(new CustomEvent('novatek_blogs_updated'));
      } else {
        alert(res.message || 'Delete failed.');
      }
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  };

  const filteredBlogs = React.useMemo(() => {
    const list = blogs.filter((b) => {
      const matchesCategory = categoryFilter === 'All' || b.category === categoryFilter;
      const matchesStatus = statusFilter === 'All' || b.status === statusFilter;
      const matchesSearch = (b.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (b.author || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (b.summary || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesStatus && matchesSearch;
    });
    return [...list].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      return (b.id || 0) - (a.id || 0);
    });
  }, [blogs, categoryFilter, statusFilter, searchQuery]);

  return (
    <>
      <div className="space-y-6 animate-fadeIn">
      
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900">Insights & News Articles Manager</h1>
          <p className="font-sans text-xs text-slate-500">Post new technical insights, edit authors, upload featured article images, or manage engineering updates.</p>
        </div>
        <button
          onClick={handleOpenAddModal}
          className="bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold uppercase px-4 py-2.5 rounded-lg shadow transition-all flex items-center gap-2 cursor-pointer"
        >
          <span className="material-symbols-outlined text-base">post_add</span>
          <span>Post New Article</span>
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="relative w-full md:w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
          <input
            type="text"
            placeholder="Search by article title or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:border-secondary"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded text-xs text-slate-800 outline-none font-mono"
          >
            <option value="All">All Categories</option>
            <option value="Railway Engineering">Railway Engineering</option>
            <option value="Industrial Automation">Industrial Automation</option>
            <option value="Aerospace Testing">Aerospace Testing</option>
            <option value="Precision Manufacturing">Precision Manufacturing</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded text-xs text-slate-800 outline-none font-mono"
          >
            <option value="All">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Blogs Data Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
            <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
              <tr>
                <th className="p-4">FEATURED IMAGE</th>
                <th className="p-4">ARTICLE TITLE & SUMMARY</th>
                <th className="p-4">AUTHOR & ROLE</th>
                <th className="p-4">CATEGORY</th>
                <th className="p-4">DATE & READ TIME</th>
                <th className="p-4">STATUS</th>
                <th className="p-4 text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-mono">
              {filteredBlogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-slate-400 font-mono">No blog articles found matching filter.</td>
                </tr>
              ) : (
                filteredBlogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-slate-50 transition-colors">
                    
                    {/* Thumbnail Image */}
                    <td className="p-4">
                      <div className="h-14 w-20 bg-slate-100 rounded-lg overflow-hidden border border-slate-200 shrink-0">
                        {blog.image ? (
                          <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <span className="material-symbols-outlined text-lg">image</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Title & Summary */}
                    <td className="p-4 max-w-xs">
                      <div className="font-serif font-bold text-slate-900 text-sm line-clamp-1">{blog.title}</div>
                      <div className="text-slate-500 font-sans text-[11px] line-clamp-2 mt-0.5">{blog.summary}</div>
                    </td>

                    {/* Author */}
                    <td className="p-4 font-mono text-slate-700">
                      <div className="font-bold text-slate-800">{blog.author}</div>
                    </td>

                    {/* Category */}
                    <td className="p-4">
                      <span className="font-mono text-[10px] font-bold bg-orange-50 text-secondary border border-orange-200 px-2.5 py-0.5 rounded">
                        {blog.category || 'Engineering'}
                      </span>
                    </td>

                    {/* Date & Read Time */}
                    <td className="p-4 font-mono text-slate-500 text-[11px]">
                      <div>{blog.date}</div>
                      <div className="text-[10px] text-slate-400">{blog.readTime || '5 min read'}</div>
                    </td>

                    {/* Status */}
                    <td className="p-4">
                      <span className={`font-mono text-[10px] font-bold px-2.5 py-1 rounded uppercase ${
                        blog.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}>
                        {blog.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="p-4 text-center">
                      <div className="flex justify-center gap-1.5">
                        <button
                          onClick={() => handleOpenEditModal(blog)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all border border-slate-200 cursor-pointer"
                          title="Edit Article"
                        >
                          <span className="material-symbols-outlined text-sm">edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteBlog(blog.id, blog.title)}
                          className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-all border border-red-200 cursor-pointer"
                          title="Delete Article"
                        >
                          <span className="material-symbols-outlined text-sm">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {/* Create / Edit Blog Article Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/40 backdrop-blur-md animate-fadeIn">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-5xl w-full p-6 sm:p-7 space-y-5 max-h-[82vh] overflow-y-auto custom-scrollbar my-auto text-slate-800">
            
            {/* Header */}
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <span className="font-mono text-xs font-bold text-secondary uppercase tracking-widest block">CONTENT PUBLISHING BUREAU</span>
                <h3 className="font-serif text-2xl font-bold text-slate-900">
                  {editingBlog ? 'Edit Blog Article' : 'Post New Blog Article'}
                </h3>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-700 p-1.5 hover:bg-slate-100 rounded-full transition-all">
                <span className="material-symbols-outlined text-2xl">close</span>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmitForm} className="space-y-4 text-xs font-sans">
              
              {/* Top 2-Column Grid for Compact Height */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                
                {/* Left Column: Title & Excerpt */}
                <div className="space-y-3">
                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      ARTICLE TITLE *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g. Next-Gen End-of-Line Railway Testing: Dynamic Load Simulation"
                      className="w-full px-3.5 py-2 text-sm bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:bg-white focus:border-secondary outline-none font-serif"
                    />
                  </div>

                  <div>
                    <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      SUMMARY / SHORT EXCERPT *
                    </label>
                    <textarea
                      rows="3"
                      required
                      value={formData.summary}
                      onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                      placeholder="Provide a concise 2-line summary of the engineering breakthrough or topic..."
                      className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-sans text-xs outline-none focus:bg-white focus:border-secondary"
                    ></textarea>
                  </div>
                </div>

                {/* Right Column: Metadata Details */}
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        CATEGORY *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      >
                        <option value="Railway Engineering">Railway Engineering</option>
                        <option value="Industrial Automation">Industrial Automation</option>
                        <option value="Aerospace Testing">Aerospace Testing</option>
                        <option value="Precision Manufacturing">Precision Manufacturing</option>
                        <option value="Software & AI">Software & AI</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        STATUS
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      >
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        BADGE / FEATURED
                      </label>
                      <select
                        value={formData.badge}
                        onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      >
                        <option value="Article">Regular Article</option>
                        <option value="Featured">Featured Post</option>
                        <option value="New Tech">New Tech</option>
                        <option value="Case Study">Case Study</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        POST DATE *
                      </label>
                      <input
                        type="date"
                        required
                        value={parseToDateInputVal(formData.date)}
                        onChange={(e) => setFormData({ ...formData, date: formatToDisplayDate(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        AUTHOR NAME & TITLE *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        placeholder="e.g. Dr. Aris Thorne"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        READ TIME
                      </label>
                      <input
                        type="text"
                        value={formData.readTime}
                        onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                        placeholder="5 min read"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        KEYWORDS / SEARCH TAGS
                      </label>
                      <input
                        type="text"
                        value={formData.keywords || ''}
                        onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                        placeholder="e.g. railway, load cell, pantograph, testing"
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 outline-none focus:bg-white focus:border-secondary font-mono"
                      />
                    </div>
                  </div>
                </div>

              </div>

              {/* Full Content */}
              <div>
                <style>{`
                  .rich-editor[contenteditable]:empty:before {
                    content: attr(placeholder);
                    color: #94a3b8;
                    pointer-events: none;
                    display: block;
                  }
                  .rich-editor ul {
                    list-style-type: disc !important;
                    padding-left: 24px !important;
                    margin-top: 8px !important;
                    margin-bottom: 8px !important;
                  }
                  .rich-editor ol {
                    list-style-type: decimal !important;
                    padding-left: 24px !important;
                    margin-top: 8px !important;
                    margin-bottom: 8px !important;
                  }
                  .rich-editor strong {
                    font-weight: bold !important;
                  }
                  .rich-editor em {
                    font-style: italic !important;
                  }
                  .rich-editor u {
                    text-decoration: underline !important;
                  }
                  .rich-editor font[size="1"] { font-size: 10px !important; }
                  .rich-editor font[size="2"] { font-size: 12px !important; }
                  .rich-editor font[size="3"] { font-size: 14px !important; }
                  .rich-editor font[size="4"] { font-size: 16px !important; }
                  .rich-editor font[size="5"] { font-size: 18px !important; }
                  .rich-editor font[size="6"] { font-size: 24px !important; }
                  .rich-editor font[size="7"] { font-size: 32px !important; }
                `}</style>
                <div className="flex justify-between items-center mb-1">
                  <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    FULL ARTICLE CONTENT * (Rich WYSIWYG Editor)
                  </label>
                  
                  {/* Rich Text Toolbar */}
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 select-none font-mono">
                    <button
                      type="button"
                      onClick={() => executeCommand('bold')}
                      className="p-1 hover:bg-slate-200 rounded font-bold text-slate-800 text-[11px] cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand('italic')}
                      className="p-1 hover:bg-slate-200 rounded italic text-slate-800 text-[11px] cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand('underline')}
                      className="p-1 hover:bg-slate-200 rounded underline text-slate-800 text-[11px] cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Underline"
                    >
                      U
                    </button>
                    <span className="w-px h-3.5 bg-slate-300 mx-1"></span>
                    <input
                      type="text"
                      list="font-sizes"
                      value={fontSizeInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFontSizeInput(val);
                        const presets = ['10', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];
                        if (presets.includes(val)) {
                          handleApplyFontSize(val);
                          setFontSizeInput('');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyFontSize(fontSizeInput);
                          setFontSizeInput('');
                        }
                      }}
                      onBlur={() => {
                        if (fontSizeInput) {
                          handleApplyFontSize(fontSizeInput);
                          setFontSizeInput('');
                        }
                      }}
                      placeholder="14"
                      className="w-12 bg-white border border-slate-300 text-[11px] text-slate-800 font-bold outline-none rounded py-0.5 text-center font-mono"
                      title="Font Size (Type & press Enter, or select)"
                    />
                    <datalist id="font-sizes">
                      <option value="10" />
                      <option value="12" />
                      <option value="14" />
                      <option value="16" />
                      <option value="18" />
                      <option value="20" />
                      <option value="24" />
                      <option value="28" />
                      <option value="32" />
                      <option value="36" />
                      <option value="48" />
                      <option value="72" />
                    </datalist>
                    <span className="w-px h-3.5 bg-slate-300 mx-1"></span>
                    <button
                      type="button"
                      onClick={() => executeCommand('insertUnorderedList')}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Bullet List"
                    >
                      <span className="material-symbols-outlined text-[14px]">format_list_bulleted</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter Link URL:');
                        if (url) executeCommand('createLink', url);
                      }}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Link"
                    >
                      <span className="material-symbols-outlined text-[14px]">link</span>
                    </button>
                    <span className="w-px h-3.5 bg-slate-300 mx-1"></span>
                    <button
                      type="button"
                      onClick={() => executeCommand('justifyLeft')}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Align Left"
                    >
                      <span className="material-symbols-outlined text-[14px]">format_align_left</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand('justifyCenter')}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Align Center"
                    >
                      <span className="material-symbols-outlined text-[14px]">format_align_center</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand('justifyRight')}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Align Right"
                    >
                      <span className="material-symbols-outlined text-[14px]">format_align_right</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => executeCommand('justifyFull')}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Align Justify"
                    >
                      <span className="material-symbols-outlined text-[14px]">format_align_justify</span>
                    </button>
                    <span className="w-px h-3.5 bg-slate-300 mx-1"></span>
                    
                    <input
                      type="file"
                      ref={inlineImageInputRef}
                      accept="image/*"
                      onChange={handleInlineImageUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => inlineImageInputRef.current && inlineImageInputRef.current.click()}
                      className="p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5"
                      title="Insert Image"
                    >
                      <span className="material-symbols-outlined text-[14px]">image</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => resizeSelectedImage('decrease')}
                      className={`p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5 ${!selectedEditorImg ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Reduce Image Size"
                      disabled={!selectedEditorImg}
                    >
                      <span className="material-symbols-outlined text-[14px]">zoom_out</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => resizeSelectedImage('increase')}
                      className={`p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5 ${!selectedEditorImg ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Increase Image Size"
                      disabled={!selectedEditorImg}
                    >
                      <span className="material-symbols-outlined text-[14px]">zoom_in</span>
                    </button>
                    <span className="w-px h-3.5 bg-slate-300 mx-1"></span>
                    <button
                      type="button"
                      onClick={() => alignSelectedImage('left')}
                      className={`p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5 ${!selectedEditorImg ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Move Image Left"
                      disabled={!selectedEditorImg}
                    >
                      <span className="material-symbols-outlined text-[14px]">align_horizontal_left</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => alignSelectedImage('center')}
                      className={`p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5 ${!selectedEditorImg ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Center Image"
                      disabled={!selectedEditorImg}
                    >
                      <span className="material-symbols-outlined text-[14px]">align_horizontal_center</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => alignSelectedImage('right')}
                      className={`p-1 hover:bg-slate-200 rounded text-slate-800 cursor-pointer flex items-center justify-center h-5 w-5 ${!selectedEditorImg ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title="Move Image Right"
                      disabled={!selectedEditorImg}
                    >
                      <span className="material-symbols-outlined text-[14px]">align_horizontal_right</span>
                    </button>
                  </div>
                </div>
                
                <div
                  contentEditable
                  ref={editorRef}
                  onInput={handleEditorInput}
                  onClick={handleEditorClick}
                  onDrop={handleEditorDrop}
                  onDragEnd={handleEditorDrop}
                  placeholder="Write full technical article content..."
                  className="rich-editor w-full p-4 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-sans text-sm outline-none focus:bg-white focus:border-secondary leading-relaxed custom-scrollbar min-h-[350px] max-h-[550px] overflow-y-auto"
                />
              </div>

              {/* Article Images & Gallery Toolbar */}
              <div className="space-y-2.5 pt-2 border-t border-slate-100">
                <div className="flex justify-between items-center">
                  <label className="block font-mono text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                    ARTICLE IMAGES GALLERY ({formData.galleryImages?.length || 0} ATTACHED)
                  </label>
                  <div className="flex gap-1 font-mono text-[11px]">
                    <button
                      type="button"
                      onClick={() => setImageTab('upload')}
                      className={`px-3 py-1 rounded cursor-pointer ${imageTab === 'upload' ? 'bg-secondary text-white font-bold' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      Upload Files
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageTab('media')}
                      className={`px-3 py-1 rounded cursor-pointer ${imageTab === 'media' ? 'bg-secondary text-white font-bold' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      Import from Media
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageTab('url')}
                      className={`px-3 py-1 rounded cursor-pointer ${imageTab === 'url' ? 'bg-secondary text-white font-bold' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      Add URL
                    </button>
                  </div>
                </div>

                {/* Tab 1: Upload Multiple Files */}
                {imageTab === 'upload' && (
                  <div className="border border-dashed border-slate-300 hover:border-secondary rounded-xl p-3 text-center bg-slate-50 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleMultipleImageUploads}
                      className="hidden"
                      id="blog-image-file-input"
                    />
                    <label htmlFor="blog-image-file-input" className="cursor-pointer block space-y-0.5">
                      <span className="material-symbols-outlined text-secondary text-xl">collections</span>
                      <div className="text-slate-800 font-bold text-xs">Click or drag multiple image files here</div>
                      <div className="text-slate-500 text-[10px] font-mono">PNG, JPG, WEBP</div>
                    </label>
                  </div>
                )}

                {/* Tab 2: Import from Media Library */}
                {imageTab === 'media' && (
                  <div className="space-y-1">
                    <div className="grid grid-cols-5 gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200 max-h-36 overflow-y-auto custom-scrollbar">
                      {mediaItems.length === 0 ? (
                        <div className="col-span-5 text-slate-400 font-mono text-[10px] p-2 text-center">No images uploaded in Media Library.</div>
                      ) : (
                        mediaItems.map((med) => {
                          const isAttached = (formData.galleryImages || []).includes(med.file_path);
                          return (
                            <div 
                              key={med.id} 
                              onClick={() => handleToggleMediaImage(med.file_path)}
                              className={`relative h-14 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                                isAttached ? 'border-secondary ring-2 ring-orange-500/50' : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                              }`}
                            >
                              <img src={med.file_path} alt={med.original_name} className="w-full h-full object-cover" />
                              {isAttached && (
                                <span className="absolute top-1 right-1 bg-secondary text-white rounded-full h-3.5 w-3.5 flex items-center justify-center text-[9px]">
                                  ✓
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* Tab 3: Direct URL */}
                {imageTab === 'url' && (
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="Paste image URL (https://...)"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 font-mono text-xs outline-none focus:border-secondary"
                    />
                    <button
                      type="button"
                      onClick={handleAddUrlImage}
                      className="px-4 py-2 bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold rounded-lg shrink-0 cursor-pointer"
                    >
                      + Add Image
                    </button>
                  </div>
                )}

                {/* Attached Gallery Grid Preview */}
                {(formData.galleryImages && formData.galleryImages.length > 0) && (
                  <div className="space-y-1">
                    <span className="text-[10px] font-mono text-slate-500 block font-bold">ATTACHED GALLERY (First is Cover):</span>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      {formData.galleryImages.map((imgUrl, idx) => (
                        <div key={idx} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-white h-16 flex flex-col">
                          <img src={imgUrl} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-opacity p-1 flex flex-col justify-center items-center gap-1 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveGalleryImage(idx)}
                              className="bg-red-600 text-white px-1 py-0.5 rounded text-[9px] hover:bg-red-700 font-mono w-full"
                            >
                              Remove
                            </button>
                            {idx !== 0 && (
                              <button
                                type="button"
                                onClick={() => handleSetCoverImage(imgUrl)}
                                className="bg-secondary text-white px-1 py-0.5 rounded text-[9px] hover:bg-orange-600 font-mono w-full"
                              >
                                Cover
                              </button>
                            )}
                          </div>
                          {idx === 0 && (
                            <span className="absolute top-0.5 left-0.5 bg-orange-600 text-white text-[8px] font-mono font-bold px-1 rounded shadow">
                              COVER
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 text-xs font-mono font-bold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-xl hover:bg-slate-100 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-secondary hover:bg-orange-600 text-white font-mono text-xs font-bold rounded-xl shadow transition-all cursor-pointer flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-base">check</span>
                  <span>{editingBlog ? 'Save Article Changes' : 'Publish Blog Article'}</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}
    </>
  );
};
